// Mapping between the two clocks this app has to reconcile:
//
//   performance.now()          DOM events, milliseconds since page load
//   AudioContext.currentTime   audio scheduling, seconds since context start
//
// Tone schedules audio ahead of real time, so a note the sequencer callback
// "plays" has not been heard yet. Anything comparing a human response against
// a sound must convert through here rather than subtracting the two directly.
//
// The mapping is sampled PER TRIAL rather than once, because: the audio clock
// is a separate crystal and drifts tens of ppm against the system clock (up to
// ~30 ms over a ten-minute session); iOS suspends and resumes the context on
// any interruption, which resets the relationship; and connecting a Bluetooth
// device can renegotiate the sample rate and invalidate everything. Each
// sample is logged, so drift and resets are visible in the CSV as a series.
//
// See docs/timing.md for what is and is not trustworthy per platform.

import * as Tone from 'tone'

export function rawContext() {
  return Tone.getContext().rawContext
}

// A snapshot of the audio/performance relationship at one instant.
// `method` records how it was obtained, because the three routes have
// materially different accuracy and week 10 needs to be able to see which.
export function sampleClockPair() {
  const c = rawContext()
  const ts = typeof c.getOutputTimestamp === 'function' ? c.getOutputTimestamp() : null

  if (ts && ts.contextTime != null && ts.performanceTime != null && ts.contextTime > 0) {
    // getOutputTimestamp already accounts for output latency: it reports when
    // audio at contextTime actually leaves the output.
    const pair = {
      ctxTime: ts.contextTime,
      perfMs: ts.performanceTime,
      method: 'output_timestamp',
    }
    // Cross-check against the naive route; these should differ by roughly
    // outputLatency. A wild value means the platform is lying somewhere.
    const naive = performance.now() - c.currentTime * 1000
    pair.consistencyMs = (pair.perfMs - pair.ctxTime * 1000) - naive + (c.outputLatency || 0) * 1000
    return pair
  }

  // Fallback: read both clocks as close together as possible and add whatever
  // latency estimate the platform will admit to.
  const perfMs = performance.now()
  const ctxTime = c.currentTime
  const latency = c.outputLatency || c.baseLatency || 0
  return {
    ctxTime: ctxTime - latency,
    perfMs,
    method: latency ? 'latency_props' : 'none',
    consistencyMs: null,
  }
}

let current = null

export function syncClocks() {
  current = sampleClockPair()
  return current
}

export function currentPair() {
  return current ?? syncClocks()
}

// Convert an AudioContext time (seconds) to the performance.now() timeline,
// using a specific clock pair (pass the trial's own pair for analysis).
export function audioToPerf(audioTime, pair = currentPair()) {
  return pair.perfMs + (audioTime - pair.ctxTime) * 1000
}

export function perfToAudio(perfMs, pair = currentPair()) {
  return pair.ctxTime + (perfMs - pair.perfMs) / 1000
}

// Await one animation frame, but never wait forever: a backgrounded tab stops
// firing rAF entirely, and a study round that awaits a frame between trials
// would deadlock rather than merely run slowly.
export function nextFrame(timeoutMs = 250) {
  return new Promise((resolve) => {
    let settled = false
    const done = (painted) => { if (!settled) { settled = true; resolve(painted) } }
    const timer = setTimeout(() => done(false), timeoutMs)
    requestAnimationFrame(() => { clearTimeout(timer); done(true) })
  })
}

// --- device characterisation, measured once per session -------------------

// performance.now() is clamped differently per engine (Safari to 1 ms, Chrome
// to ~100 µs). Worth recording so nobody wonders why the Safari rows are all
// whole numbers.
export function measureClockResolution() {
  const t0 = performance.now()
  let t1 = t0
  // eslint-disable-next-line no-empty
  while ((t1 = performance.now()) === t0) {}
  return t1 - t0
}

export const ASSUMED_DISPLAY_PERIOD_MS = 16.7

// Median frame interval. Do not assume 60 Hz: phones run 120 Hz, and Low Power
// Mode drops to ~30.
//
// rAF does not fire at all in a backgrounded tab, so this must never be the
// thing a session waits on forever: it gives up and returns the nominal 60 Hz
// value rather than hanging on a blank screen.
export function measureDisplayPeriod(frames = 20, timeoutMs = 1500) {
  return new Promise((resolve) => {
    const deltas = []
    let last = null
    let n = 0
    let settled = false
    const finish = (value) => { if (!settled) { settled = true; resolve(value) } }
    const timer = setTimeout(() => {
      deltas.sort((a, b) => a - b)
      finish(deltas.length ? deltas[Math.floor(deltas.length / 2)] : ASSUMED_DISPLAY_PERIOD_MS)
    }, timeoutMs)
    const step = (t) => {
      if (last !== null) deltas.push(t - last)
      last = t
      if (++n < frames) requestAnimationFrame(step)
      else {
        clearTimeout(timer)
        deltas.sort((a, b) => a - b)
        finish(deltas[Math.floor(deltas.length / 2)] ?? ASSUMED_DISPLAY_PERIOD_MS)
      }
    }
    requestAnimationFrame(step)
  })
}

export function audioEnvironment() {
  const c = rawContext()
  return {
    audio_base_latency_ms: (c.baseLatency ?? 0) * 1000,
    audio_output_latency_ms: c.outputLatency != null ? c.outputLatency * 1000 : '',
    audio_sample_rate: c.sampleRate,
    tone_lookahead_ms: Tone.getContext().lookAhead * 1000,
    ctx_state: c.state,
  }
}

// `event.timeStamp` is preferred over calling performance.now() in a handler:
// it is when the platform delivered the input, not when our handler got the
// main thread back. Under audio + camera load those differ by tens of ms, and
// that difference would otherwise be charged to the participant.
export function eventTime(event) {
  const ts = event?.timeStamp
  // An epoch-based stamp (old WebKit) is not on the performance timeline.
  if (typeof ts !== 'number' || ts <= 0 || ts > 1e12) {
    return { t: performance.now(), source: 'handler' }
  }
  return { t: ts, source: 'event' }
}

export function dispatchDelay(event) {
  const { t, source } = eventTime(event)
  return source === 'event' ? performance.now() - t : 0
}
